import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
    LayoutDashboard,
    Users,
    Package,
    Briefcase,
    FileText,
    FileSpreadsheet,
    ShoppingCart,
    PackageCheck,
    Receipt,
    Warehouse,
    Trash2,
    LayoutGrid,
    UserCog,
} from "lucide-react";

// Roles restricted to only creating/viewing PRs — no full purchasing sidebar
const REQUESTER_ONLY_ROLES = new Set(["requester"]);

// Central page-access matrix — mirrored by App.js route guards and enforced
// server-side via block_roles() on the corresponding list endpoints.
// Adding/removing a role here must be paired with the matching backend list route.
const ALL_PURCH = ["admin", "purchasing_manager", "purchasing_specialist",
                   "warehouse", "finance",
                   "project_manager", "general_manager", "auditor"];
const PURCH_TEAM = ["admin", "purchasing_manager", "purchasing_specialist"];
const APPROVERS = ["admin", "purchasing_manager", "purchasing_specialist"];

const groups = [
    {
        label: "Genel",
        items: [
            { to: "/", icon: LayoutDashboard, label: "Kontrol Paneli", exact: true, testid: "nav-dashboard",
              roles: ALL_PURCH },
        ],
    },
    {
        label: "Satın Alma",
        items: [
            { to: "/purchase-requests", icon: FileText, label: "Talepler", testid: "nav-prs",
              roles: ["admin", "purchasing_manager", "purchasing_specialist",
                      "project_manager", "general_manager",
                      "auditor", "requester", "warehouse"] },
            { to: "/rfqs", icon: FileSpreadsheet, label: "RFQ & Teklifler", testid: "nav-rfqs",
              roles: [...PURCH_TEAM, "general_manager", "auditor"] },
            { to: "/purchase-orders", icon: ShoppingCart, label: "Siparişler", testid: "nav-pos",
              roles: [...PURCH_TEAM, "general_manager", "finance", "auditor"] },
        ],
    },
    {
        label: "Operasyon",
        items: [
            // Mal Kabul: Müdür bu bölümde YOK (Eyes Çelik spec)
            { to: "/goods-receipts", icon: PackageCheck, label: "Mal Kabul", testid: "nav-grs",
              roles: ["admin", "warehouse", "purchasing_specialist", "auditor"] },
            // Fatura: Müdür bu bölümde YOK (Eyes Çelik spec)
            { to: "/invoices", icon: Receipt, label: "Faturalar & 3'lü Eşleştirme", testid: "nav-inv",
              roles: ["admin", "finance", "purchasing_specialist", "auditor"] },
            { to: "/stock", icon: Warehouse, label: "Stok", testid: "nav-stock",
              roles: ["admin", "warehouse", "purchasing_manager", "purchasing_specialist", "auditor"] },
        ],
    },
    {
        label: "Ana Veri",
        items: [
            { to: "/suppliers", icon: Users, label: "Tedarikçiler", testid: "nav-suppliers",
              roles: [...PURCH_TEAM, "auditor"] },
            { to: "/products", icon: Package, label: "Ürünler", testid: "nav-products",
              roles: [...PURCH_TEAM, "warehouse", "auditor"] },
            { to: "/projects", icon: Briefcase, label: "Projeler", testid: "nav-projects",
              roles: [...PURCH_TEAM, "project_manager", "general_manager", "auditor"] },
        ],
    },
    {
        label: "Sistem",
        items: [
            { to: "/admin/users", icon: UserCog, label: "Kullanıcı Yönetimi", testid: "nav-admin-users",
              roles: ["admin"] },
            { to: "/recycle-bin", icon: Trash2, label: "Geri Dönüşüm Kutusu", testid: "nav-recycle",
              roles: ["admin", "purchasing_manager", "finance", "auditor"] },
        ],
    },
];

function itemVisibleFor(item, role) {
    if (item.roles && !item.roles.includes(role)) return false;
    if (item.hideForRoles && item.hideForRoles.includes(role)) return false;
    return true;
}

export default function Sidebar() {
    const loc = useLocation();
    const { user } = useAuth();
    const role = user?.role || "";
    const visibleGroups = groups
        .map((g) => ({ ...g, items: g.items.filter((it) => itemVisibleFor(it, role)) }))
        .filter((g) => g.items.length > 0);
    return (
        <aside
            data-testid="app-sidebar"
            className="hidden md:flex fixed left-0 top-0 bottom-0 w-64 flex-col text-[color:hsl(var(--sidebar-foreground))]"
            style={{ background: "hsl(var(--sidebar))", borderRight: "1px solid hsl(var(--sidebar-border))" }}
        >
            <div className="px-4 py-4 flex items-center gap-3 border-b" style={{ borderColor: "hsl(var(--sidebar-border))" }}>
                <div className="inline-flex items-center bg-white rounded-md px-2.5 py-1.5 shadow-sm">
                    <img
                        src="/brand/eyes-celik-logo-trimmed.webp"
                        alt="Eyes Çelik"
                        className="h-8 w-auto block"
                        data-testid="sidebar-brand-logo"
                    />
                </div>
                <div className="text-[9px] leading-tight uppercase tracking-widest font-medium" style={{ color: "hsl(var(--sidebar-muted))" }}>
                    Satın Alma<br/>Yönetim Sistemi
                </div>
            </div>
            <nav className="flex-1 overflow-y-auto py-3 px-2">
                <div className="mb-3 px-2">
                    <NavLink
                        to="/portal"
                        data-testid="nav-portal"
                        className="flex items-center gap-3 px-3 py-2 rounded-md text-[13px] transition-colors border"
                        style={{
                            color: "hsl(var(--sidebar-foreground) / 0.85)",
                            borderColor: "hsl(var(--sidebar-border))",
                            background: "hsl(var(--sidebar-active) / 0.06)",
                        }}
                    >
                        <LayoutGrid size={15} strokeWidth={2} />
                        <span>Portal'a Dön</span>
                    </NavLink>
                </div>
                {visibleGroups.map((g) => (
                    <div key={g.label} className="mb-4">
                        <div className="px-3 pt-2 pb-1.5 text-[10px] uppercase font-medium tracking-widest"
                             style={{ color: "hsl(var(--sidebar-muted))" }}>
                            {g.label}
                        </div>
                        <div className="flex flex-col gap-0.5">
                            {g.items.map((it) => {
                                const Icon = it.icon;
                                const active = it.exact
                                    ? loc.pathname === it.to
                                    : loc.pathname.startsWith(it.to);
                                return (
                                    <NavLink
                                        key={it.to}
                                        to={it.to}
                                        data-testid={it.testid}
                                        className={`group flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] transition-colors ${
                                            active ? "font-medium text-white" : ""
                                        }`}
                                        style={
                                            active
                                                ? { background: "hsl(var(--sidebar-active) / 0.15)", color: "white", borderLeft: "3px solid hsl(var(--sidebar-active))", paddingLeft: "9px" }
                                                : { color: "hsl(var(--sidebar-foreground) / 0.85)" }
                                        }
                                    >
                                        <Icon size={16} strokeWidth={2} />
                                        <span>{it.label}</span>
                                    </NavLink>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </nav>
            <div className="px-4 py-3 text-[11px] border-t" style={{ borderColor: "hsl(var(--sidebar-border))", color: "hsl(var(--sidebar-muted))" }}>
                v1.0 • Phase 1 MVP
            </div>
        </aside>
    );
}
